import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { ConnectionStatus } from '../../../shared/enums/app-constants';

// Re-export ConnectionStatus for convenience
export { ConnectionStatus };

/**
 * User presence information
 */
export interface CollaborationUser {
  name: string;
  color: string;
  clientId: number;
  isEditing?: boolean;
  lastEditTime?: number;
  avatar?: string | null;
  selection?: {
    start: number;
    end: number;
  } | null;
}

/**
 * Service for managing Yjs-based real-time collaboration
 * Handles WebSocket connection, document syncing, and user presence
 */
@Injectable({
  providedIn: 'root'
})
export class YjsCollaborationService implements OnDestroy {
  private logger = inject(LoggerService);
  
  // WebSocket server configuration
  private readonly WS_URL = 'wss://yjs-api.optimastride.com';
  
  // Yjs document and provider
  private ydoc: Y.Doc | null = null;
  private provider: WebsocketProvider | null = null;
  private ytext: Y.Text | null = null;
  
  // Observable state
  connectionStatus = signal<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  isSynced = signal<boolean>(false);
  onlineUsers = signal<CollaborationUser[]>([]);
  currentUser = signal<CollaborationUser | null>(null);
  
  // Computed values
  isConnected = computed(() => this.connectionStatus() === ConnectionStatus.CONNECTED);
  onlineUserCount = computed(() => this.onlineUsers().length);
  
  // Callbacks for content changes
  private contentChangeCallback: ((content: string) => void) | null = null;
  private isLocalUpdate = false;
  private statusListener?: (event: { status: string }) => void;
  private syncListener?: (isSynced: boolean) => void;
  private awarenessChangeListener?: () => void;
  private contentObserver?: (event: Y.YTextEvent) => void;

  /**
   * Connect to a collaboration room
   * @param bizId - Business entity ID used to create room ID
   * @param userName - Current user's name
   * @param userColor - Current user's color indicator
   * @param initialContent - Initial content to seed the YJS document with (from database)
   * @param onContentChange - Callback when content changes from remote
   */
  connect(
    bizEntity: IBizEntity,
    userName: string = 'Anonymous',
    userColor: string = '#3b82f6',
    initialContent?: string,
    onContentChange?: (content: string) => void
  ): void {
    if (this.provider) {
      this.logger.warn('⚠️ Already connected to a room. Disconnect first.');
      return;
    }

    try {
      const roomId = `wb:${bizEntity.id}`;
      this.logger.info(`🔌 Connecting to collaboration room: ${roomId}`);
      
      // Create Yjs document
      this.ydoc = new Y.Doc();
      
      // Connect to WebSocket server
      this.provider = new WebsocketProvider(
        this.WS_URL,
        roomId,
        this.ydoc
      );
      
      // Get shared text for markdown content
      this.ytext = this.ydoc.getText('content');
      
      // Set up connection status listeners
      this.setupConnectionListeners(initialContent);
      
      // Set up content change listeners
      if (onContentChange) {
        this.contentChangeCallback = onContentChange;
        this.setupContentListener();
      }
      
      // Set up user presence
      this.setupUserPresence(userName, userColor, bizEntity);
      
      this.logger.info('✅ Collaboration initialized');
      
    } catch (error) {
      this.logger.error(`❌ Failed to connect to collaboration room: ${error}`);
      this.connectionStatus.set(ConnectionStatus.DISCONNECTED);
    }
  }

  /**
   * Set up connection event listeners
   * @param initialContent - Initial content from database to seed if YJS is empty
   */
  private setupConnectionListeners(initialContent?: string): void {
    if (!this.provider) return;
    
    this.statusListener = ({ status }: { status: string }) => {
      this.logger.debug(`📡 Connection status: ${status}`);
      
      if (status === ConnectionStatus.CONNECTED) {
        this.connectionStatus.set(ConnectionStatus.CONNECTED);
      } else if (status === ConnectionStatus.CONNECTING) {
        this.connectionStatus.set(ConnectionStatus.CONNECTING);
      } else {
        this.connectionStatus.set(ConnectionStatus.DISCONNECTED);
      }
    };
    this.provider.on('status', this.statusListener);
    
    this.syncListener = (isSynced: boolean) => {
      this.isSynced.set(isSynced);
      if (isSynced) {
        this.logger.info('🔄 Document synced with server');
        
        // Check if YJS document is empty and we have initial content from database
        if (this.ytext && initialContent !== undefined) {
          const currentYjsContent = this.ytext.toString();
          
          // If YJS is empty but we have database content, seed it
          if (currentYjsContent.length === 0 && initialContent.length > 0) {
            this.logger.info('📝 YJS document is empty, seeding with database content');
            this.isLocalUpdate = true;
            this.ydoc!.transact(() => {
              this.ytext!.insert(0, initialContent);
            });
            this.logger.info('✅ Database content loaded into YJS document');
          } else if (currentYjsContent.length > 0) {
            this.logger.info('✅ Using existing YJS content from active session');
            // If there's content in YJS, it means someone else is editing
            // Trigger callback to update editor with the active session content
            if (this.contentChangeCallback) {
              this.contentChangeCallback(currentYjsContent);
            }
          }
        }
      }
    };
    this.provider.on('sync', this.syncListener);
  }

  /**
   * Set up content change listener
   */
  private setupContentListener(): void {
    if (!this.ytext) return;
    
    this.contentObserver = () => {
      this.logger.info(`========== YJS OBSERVE TRIGGERED ==========`);
      this.logger.info(`Is local update: ${this.isLocalUpdate}`);
      this.logger.info(`Has callback: ${!!this.contentChangeCallback}`);
      
      const content = this.ytext!.toString();
      this.logger.info(`Current Yjs content: ${content.length} characters`);
      
      // Only trigger callback for remote changes (not local ones)
      if (!this.isLocalUpdate && this.contentChangeCallback) {
        this.logger.info(`TRIGGERING REMOTE CHANGE CALLBACK`);
        this.contentChangeCallback(content);
      } else {
        if (this.isLocalUpdate) {
          this.logger.info(`Skipping - local update`);
        }
      }
      
      // Always reset the flag after processing
      this.isLocalUpdate = false;
    };
    this.ytext.observe(this.contentObserver);
    
    this.logger.info('Content listener attached');
  }

  /**
   * Set up user presence (awareness)
   */
  private setupUserPresence(userName: string, userColor: string, bizEntity: IBizEntity): void {
    if (!this.provider) return;
    const clientId = this.provider.awareness.clientID;
    const user: CollaborationUser = {
      name: userName,
      color: userColor,
      clientId: clientId,
      isEditing: false,
      lastEditTime: Date.now(),
        avatar: bizEntity?.ownerDetail?.avatar, // TODO: Add avatar support
    };
    
    this.currentUser.set(user);
    
    // Set local user state
    this.provider.awareness.setLocalState({ user });
    
    // Listen for awareness changes
    this.awarenessChangeListener = () => {
      const users: CollaborationUser[] = [];
      const currentClientId = this.provider!.awareness.clientID;
      
      this.provider!.awareness.getStates().forEach((state: any, clientId: number) => {
        // Only include OTHER users, not yourself
        if (state.user && clientId !== currentClientId) {
          users.push(state.user);
        }
      });
      this.onlineUsers.set(users);
      
      // Log user updates with selection info
      this.logger.info(`👥 Awareness changed: ${users.length} remote users online`);
      users.forEach(u => {
        if (u.selection) {
          this.logger.info(`   - ${u.name} has selection: ${u.selection.start}-${u.selection.end}`);
        } else {
          this.logger.info(`   - ${u.name} (no selection)`);
        }
      });
    };
    this.provider.awareness.on('change', this.awarenessChangeListener);
    
    this.logger.info('✅ User presence initialized');
  }
  
  /**
   * Update user editing status
   */
  setUserEditing(isEditing: boolean): void {
    if (!this.provider || !this.currentUser()) return;
    
    const updatedUser = {
      ...this.currentUser()!,
      isEditing,
      lastEditTime: Date.now()
    };
    
    this.currentUser.set(updatedUser);
    this.provider.awareness.setLocalState({ user: updatedUser });
    
    this.logger.debug(`✏️ User editing status: ${isEditing}`);
  }
  
  /**
   * Update user's text selection
   * Broadcasts the selection range to all connected users
   */
  updateSelection(start: number, end: number): void {
    if (!this.provider || !this.currentUser()) return;
    
    const updatedUser = {
      ...this.currentUser()!,
      selection: { start, end }, // Always send position, even for cursor-only
      lastEditTime: Date.now()
    };
    
    this.currentUser.set(updatedUser);
    this.provider.awareness.setLocalState({ user: updatedUser });
    
    if (start !== end) {
      this.logger.info(`📍 Selection updated in Yjs: ${start}-${end} (${end - start} chars)`);
      this.logger.info(`📍 Broadcasting to ${this.onlineUsers().length} users`);
    } else {
      this.logger.debug(`📍 Cursor position updated: ${start}`);
    }
  }
  
  /**
   * Clear user's selection
   */
  clearSelection(): void {
    if (!this.provider || !this.currentUser()) return;
    
    const updatedUser = {
      ...this.currentUser()!,
      selection: null
    };
    
    this.currentUser.set(updatedUser);
    this.provider.awareness.setLocalState({ user: updatedUser });
    
    this.logger.info('📍 Selection cleared');
  }

  /**
   * Update content in the shared document
   * @param content - New content to set
   */
  updateContent(content: string): void {
    if (!this.ytext || !this.ydoc) {
      this.logger.warn('⚠️ Cannot update content: not connected');
      return;
    }

    try {
      // Mark as local update to prevent triggering change callback
      this.isLocalUpdate = true;
      
      this.logger.debug(`📤 Updating Yjs content: ${content.length} characters`);
      
      // Replace all content using a transaction
      this.ydoc.transact(() => {
        const currentLength = this.ytext!.length;
        this.ytext!.delete(0, currentLength);
        this.ytext!.insert(0, content);
      });
      
      this.logger.info(`✅ Yjs content updated and broadcasted`);
      
    } catch (error) {
      this.logger.error(`❌ Failed to update content: ${error}`);
      this.isLocalUpdate = false;
    }
  }

  /**
   * Get current content from the shared document
   */
  getContent(): string {
    if (!this.ytext) {
      return '';
    }
    return this.ytext.toString();
  }

  /**
   * Insert text at a specific position
   */
  insertText(position: number, text: string): void {
    if (!this.ytext) {
      this.logger.warn('⚠️ Cannot insert text: not connected');
      return;
    }
    
    this.isLocalUpdate = true;
    this.ytext.insert(position, text);
  }

  /**
   * Delete text at a specific position
   */
  deleteText(position: number, length: number): void {
    if (!this.ytext) {
      this.logger.warn('⚠️ Cannot delete text: not connected');
      return;
    }
    
    this.isLocalUpdate = true;
    this.ytext.delete(position, length);
  }
  /**
   * Disconnect from the collaboration room
   */
  disconnect(): void {
    if (!this.provider) {
      return;
    }
    
    try {
      this.logger.info('🔌 Disconnecting from collaboration room');

      if (this.statusListener) this.provider.off('status', this.statusListener);
      if (this.syncListener) this.provider.off('sync', this.syncListener);
      if (this.awarenessChangeListener) {
        this.provider.awareness.off('change', this.awarenessChangeListener);
      }
      if (this.contentObserver && this.ytext) this.ytext.unobserve(this.contentObserver);

      this.provider.awareness.setLocalState(null);
      
      // Clean up provider
      this.provider.disconnect();
      this.provider.destroy();
      
      // Clean up document
      if (this.ydoc) {
        this.ydoc.destroy();
      }
      
      // Reset state
      this.provider = null;
      this.ydoc = null;
      this.ytext = null;
      this.contentChangeCallback = null;
      this.statusListener = undefined;
      this.syncListener = undefined;
      this.awarenessChangeListener = undefined;
      this.contentObserver = undefined;
      this.isLocalUpdate = false;
      
      this.connectionStatus.set(ConnectionStatus.DISCONNECTED);
      this.isSynced.set(false);
      this.onlineUsers.set([]);
      this.currentUser.set(null);
      
      this.logger.info('✅ Disconnected from collaboration');
      
    } catch (error) {
      this.logger.error(`❌ Error during disconnect: ${error}`);
    }
  }

  /**
   * Check if currently connected
   */
  isConnectedToRoom(): boolean {
    return this.provider !== null && this.connectionStatus() === ConnectionStatus.CONNECTED;
  }

  /**
   * Get room information
   */
  getRoomInfo(): { roomId: string | null; userCount: number } {
    return {
      roomId: this.provider?.roomname || null,
      userCount: this.onlineUsers().length
    };
  }

  /**
   * Clean up on service destroy
   */
  ngOnDestroy(): void {
    this.disconnect();
  }
}
